import Vue from 'vue';
import axios from '@axios';
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';
import { sanitizeGridItems } from '@/utils/gridUtils';

const state = {
  active: false,
  isPaused: false,
  currentTourId: null,
  currentIndex: 0,
  intervalSecs: 30,
  countdown: 0,
  name: '',
  targetTabId: null,
  isReloadingWidget: false,
  timer: null,
  countdownTimer: null,
  metadata: null,
  currentView: null,
  count: 0,
  links: {
    firstLink: null,
    lastLink: null,
    prevLink: null,
    nextLink: null
  },
  viewNames: {
    prevViewName: null,
    nextViewName: null,
    firstViewName: null,
    lastViewName: null
  },
  // do not delete, it is a flag for the operational security modal
  isCloseOperatinalSecurityFormModal: false
};

const getters = {
  videoTourIsRunning: (state) => state.active && !state.isPaused,
  videoTourIsPaused: (state) => state.active && state.isPaused,
  videoTourCurrentTourId: (state) => state.currentTourId,
  videoTourCurrentIndex: (state) => state.currentIndex,
  videoTourInterval: (state) => state.intervalSecs,
  videoTourCountdown: (state) => state.countdown,
  videoTourTotal: (state) => state.count,
  videoTourName: (state) => state.name,
  videoTourNameTab: (state) => state.name,
  videoTourPrevViewName: (state) => state.viewNames.prevViewName,
  videoTourNextViewName: (state) => state.viewNames.nextViewName,
  videoTourFirstViewName: (state) => state.viewNames.firstViewName,
  videoTourLastViewName: (state) => state.viewNames.lastViewName,
  isReloadingWidget: (state) => state.isReloadingWidget,
  isCloseOperatinalSecurityFormModal: (state) => state.isCloseOperatinalSecurityFormModal,
  isActiveTabVideoTour: (state, getters, rootState) => {
    return state.active && state.targetTabId === rootState.grid.activeTab;
  }
};

const mutations = {
  setTourActive(state, { active, paused = false }) {
    state.active = active;
    state.isPaused = paused;
  },
  setCurrentIndex(state, idx) {
    state.currentIndex = idx;
  },
  clearTimers(state) {
    if (state.timer) {
      clearInterval(state.timer);
      state.timer = null;
    }
    if (state.countdownTimer) {
      clearInterval(state.countdownTimer);
      state.countdownTimer = null;
    }
  },
  setCountdown(state, value) {
    state.countdown = value;
  },
  setTargetTabId(state, tabId) {
    state.targetTabId = tabId;
  },
  toggleReloadinVideoTourWidget(state) {
    state.isReloadingWidget = !state.isReloadingWidget;
  },
  setTourMetadata(state, metadata) {
    state.currentTourId = metadata.id;
    state.name = metadata.name;
    state.count = metadata.count;
    state.intervalSecs = metadata.interval_secs;
    state.metadata = metadata;
  },
  setCurrentView(state, payload) {
    const sanitizedView = sanitizeViewPayload(payload.view);
    state.currentView = sanitizedView;
    state.currentIndex = parseInt(payload.currentIndex, 10) || 0;
    state.count = payload.count ?? state.count;
    state.links = {
      firstLink: payload.firstLink || null,
      lastLink: payload.lastLink || null,
      prevLink: payload.prevLink || null,
      nextLink: payload.nextLink || null,
    };
    state.viewNames = {
      prevViewName: payload.prevViewName || null,
      nextViewName: payload.nextViewName || null,
      firstViewName: payload.firstViewName || null,
      lastViewName: payload.lastViewName || null,
    };
  },
  setTimer(state, timer) {
    state.timer = timer;
  },
  setCountdownTimer(state, timer) {
    state.countdownTimer = timer;
  },
  resetVideoTour(state) {
    state.active = false;
    state.isPaused = false;
    state.currentTourId = null;
    state.currentIndex = 0;
    state.countdown = 0;
    state.name = '';
    state.targetTabId = null;
    state.currentView = null;
    state.count = 0;
    state.metadata = null;
    state.links = {
      firstLink: null,
      lastLink: null,
      prevLink: null,
      nextLink: null
    };
    state.viewNames = {
      prevViewName: null,
      nextViewName: null,
      firstViewName: null,
      lastViewName: null
    };
  },
  toggleCloseOperationalSecuritFormyModal(state) {
    state.isCloseOperatinalSecurityFormModal = !state.isCloseOperatinalSecurityFormModal;
  },
};

const actions = {
  async setVideoTour({ state, commit, dispatch, rootState }, tour) {
    try {
      if (state.targetTabId && rootState.grid?.tabs?.[state.targetTabId]) {
        await dispatch('removeTab', state.targetTabId);
      } else if (state.active) {
        await dispatch('stopVideoTour');
      }

      const tabName = `${tour.name}`;
      let tabId = null;

      const creationResult = await dispatch('addNewTab', {
        tabName,
        layout: 'default',
        type: 'video-tour',
      });

      if (creationResult?.status) {
        tabId = creationResult.id;
      } else {
        tabId = Object.keys(rootState.grid.tabs || {}).find((id) => rootState.grid.tabs[id].name === tabName) || creationResult?.id;

        if (!tabId) {
          const fallbackResult = await dispatch('addNewTab', {
            tabName: `${tabName} (${Date.now()})`,
            layout: 'default',
            type: 'video-tour',
          });
          tabId = fallbackResult.id;
        }
      }

      if (!tabId) {
        notifyError('No fue posible crear la pestaña del video tour.');
        return;
      }

      commit('setTargetTabId', tabId);
      commit('updateTabType', { id: tabId, type: 'video-tour' });

      const { data: metadataResponse } = await axios.get(`v1/video-tours/metadata?video_tour_id=${tour.id}`);

      if (!metadataResponse?.success) {
        notifyError(metadataResponse?.message || 'No se pudo cargar la metadata del video tour.');
        return;
      }

      commit('setTourMetadata', metadataResponse.data);

      await dispatch('setActiveTab', tabId);

      const started = await dispatch('startVideoTourInTab', { tabId, index: 1 });

      if (!started) {
        notifyError('No fue posible iniciar el video tour.');
      }
    } catch (error) {
      console.error('Error setting video tour:', error);
      notifyError('Error al iniciar el video tour.');
    }
  },

  async startVideoTourInTab({ state, commit, dispatch }, { tabId, index = 1 } = {}) {
    if (!state.currentTourId) {
      notifyError('No hay un video tour seleccionado.');
      return false;
    }

    commit('setTargetTabId', tabId);

    const loaded = await dispatch('loadCurrentView', { index });

    if (!loaded) {
      return false;
    }

    commit('setTourActive', { active: true, paused: false });
    persistTabVideoTourState(commit, state);
    dispatch('startCountdown');
    return true;
  },

  async loadCurrentView({ state, commit, dispatch }, { index = null, link = null } = {}) {
    if (!state.currentTourId) {
      return false;
    }

    const normalizedLink = normalizeLink(link);
    const targetIndex = (index ?? state.currentIndex) || 1;

    try {
      const request = normalizedLink
        ? await axios.get(normalizedLink)
        : await axios.get(`v1/video-tours/go-to?video_tour_id=${state.currentTourId}&current_index=${targetIndex}`);

      if (!request?.data?.success) {
        notifyError(request?.data?.message || 'No se pudo obtener la vista del video tour.');
        return false;
      }

      commit('setCurrentView', request.data.data);
      persistTabVideoTourState(commit, state);
      await dispatch('applyCurrentViewToTab');
      return true;
    } catch (error) {
      console.error('Error loading current view:', error);
      notifyError('Error al obtener la vista del video tour.');
      return false;
    }
  },

  pauseVideoTour({ commit, state, dispatch }) {
    commit('clearTimers');
    commit('setTourActive', { active: true, paused: true });
    persistTabVideoTourState(commit, state);
    dispatch('updateUserConfig');
  },

  resumeVideoTour({ commit, dispatch, state }) {
    if (!state.currentTourId) {
      return;
    }
    commit('setTourActive', { active: true, paused: false });
    persistTabVideoTourState(commit, state);
    dispatch('updateUserConfig');
    dispatch('startCountdown');
  },

  stopVideoTour({ commit, state, dispatch }) {
    const tabId = state.targetTabId;
    commit('clearTimers');
    commit('resetVideoTour');
    if (tabId) {
      commit('setTabVideoTourState', { tabId, videoTour: null });
    }
    dispatch('updateUserConfig');
  },

  async nextVideoTour({ state, dispatch }) {
    if (!state.currentTourId) return;

    const hasViews = state.count > 0 || !!state.links.firstLink;
    if (!hasViews) return;

    const isAtEnd = state.count > 0
      ? state.currentIndex >= state.count
      : (!state.links.nextLink && !!state.links.firstLink);

    let targetLink = null;
    let targetIndex = state.currentIndex;

    if (isAtEnd) {
      targetLink = state.links.firstLink || null;
      targetIndex = 1;
    } else if (state.links.nextLink) {
      targetLink = state.links.nextLink;
      targetIndex = Math.min(state.currentIndex + 1, state.count || state.currentIndex + 1);
    } else {
      targetIndex = Math.min(state.currentIndex + 1, state.count || state.currentIndex + 1);
    }

    const success = await dispatch('loadCurrentView', {
      link: targetLink,
      index: targetIndex,
    });

    if (success && state.active && !state.isPaused) {
      dispatch('startCountdown');
    }
  },

  async prevVideoTour({ state, dispatch }) {
    if (!state.currentTourId) return;

    const hasViews = state.count > 0 || !!state.links.lastLink;
    if (!hasViews) return;

    const isAtStart = state.currentIndex <= 1 || (state.currentIndex === 0 && !!state.links.lastLink);

    let targetLink = null;
    let targetIndex = state.currentIndex;

    if (isAtStart) {
      targetLink = state.links.lastLink || null;
      targetIndex = state.count || state.currentIndex || 1;
    } else if (state.links.prevLink) {
      targetLink = state.links.prevLink;
      targetIndex = Math.max(state.currentIndex - 1, 1);
    } else {
      targetIndex = Math.max(state.currentIndex - 1, 1);
    }

    const success = await dispatch('loadCurrentView', {
      link: targetLink,
      index: targetIndex,
    });

    if (success && state.active && !state.isPaused) {
      dispatch('startCountdown');
    }
  },

  async firstVideoTour({ state, dispatch }) {
    if (!state.currentTourId) return;

    const success = await dispatch('loadCurrentView', {
      link: state.links.firstLink,
      index: 1,
    });

    if (success && state.active && !state.isPaused) {
      dispatch('startCountdown');
    }
  },

  async lastVideoTour({ state, dispatch }) {
    if (!state.currentTourId) return;

    const success = await dispatch('loadCurrentView', {
      link: state.links.lastLink,
      index: state.count || state.currentIndex || 1,
    });

    if (success && state.active && !state.isPaused) {
      dispatch('startCountdown');
    }
  },

  startCountdown({ state, commit, dispatch, rootState }) {
    commit('clearTimers');

    if (!state.intervalSecs || state.intervalSecs <= 0) {
      dispatch('nextVideoTour');
      return;
    }

    commit('setCountdown', state.intervalSecs);

    const countdownTimer = setInterval(() => {
      const isAltPressed = rootState.grid?.isAltPressed || false;

      if (!isAltPressed) {
        const newCountdown = state.countdown - 1;
        commit('setCountdown', newCountdown);

        if (newCountdown <= 0) {
          dispatch('nextVideoTour');
        }
      }
      // If Alt is pressed, do not decrement the countdown (pause the timer)
    }, 1000);

    commit('setCountdownTimer', countdownTimer);
  },

  async applyCurrentViewToTab({ state, commit, dispatch, rootState }) {
    if (!state.currentView || !state.targetTabId) return;

    const tabId = state.targetTabId;
    const view = state.currentView;

    const targetTabExists = !!rootState.grid?.tabs?.[tabId];
    if (!targetTabExists) return;

    commit('updateTabLayout', {
      tabId,
      layoutId: view.layout || 'default',
    });

    commit('updateTabType', {
      id: tabId,
      type: 'video-tour',
    });

    commit('updateTabLayoutGrid', {
      tabId,
      gridItems: sanitizeGridItems(view.gridItems),
    });

    commit('updateTabSlots', {
      tabId,
      slots: view.slots,
    });

    commit('updateTabHiddenSlots', {
      tabId,
      hiddenSlots: view.hiddenSlots,
    });

    commit('editTabName', {
      data: {
        id: tabId,
        name: `${state.name || view.name}`,
      },
    });

    dispatch('updateUserConfig');
  },
  async resumeVideoTourFromConfig({ state, commit, dispatch, rootState }, { tabId, videoTourConfig, activateTab = false } = {}) {
    if (!tabId || !videoTourConfig?.id) return;

    const tabExists = !!rootState.grid?.tabs?.[tabId];
    if (!tabExists) return;

    if (activateTab) {
      await dispatch('setActiveTab', tabId);
    }

    commit('setTargetTabId', tabId);

    try {
      const { data: metadataResponse } = await axios.get(`v1/video-tours/metadata?video_tour_id=${videoTourConfig.id}`);

      if (!metadataResponse?.success) {
        commit('setTabVideoTourState', { tabId, videoTour: null });
        return;
      }

      commit('setTourMetadata', metadataResponse.data);

      const indexToLoad = videoTourConfig.currentIndex && videoTourConfig.currentIndex > 0
        ? videoTourConfig.currentIndex
        : 1;

      const loaded = await dispatch('loadCurrentView', { index: indexToLoad });
      if (!loaded) return;

      const shouldBeActive = videoTourConfig.active !== undefined ? videoTourConfig.active : true;
      const shouldBePaused = videoTourConfig.isPaused !== undefined ? videoTourConfig.isPaused : false;

      commit('setTourActive', { active: shouldBeActive, paused: shouldBePaused });

      if (shouldBeActive && !shouldBePaused) {
        dispatch('startCountdown');
      } else {
        commit('setCountdown', state.intervalSecs);
      }

      persistTabVideoTourState(commit, state);
      dispatch('updateUserConfig');
    } catch (error) {
      console.error('Error resuming video tour from config:', error);
      notifyError('No se pudo reanudar el video tour.');
    }
  },
};

const sanitizeValue = (value) => {
  if (value === null || value === undefined) return value;
  if (Array.isArray(value)) {
    return value.map((item) => sanitizeValue(item));
  }
  if (typeof value === 'object') {
    return JSON.parse(JSON.stringify(value));
  }
  return value;
};

const pickKeys = (source = {}, allowedKeys = []) => allowedKeys.reduce((acc, key) => {
  if (Object.prototype.hasOwnProperty.call(source, key) && source[key] !== undefined) {
    acc[key] = sanitizeValue(source[key]);
  }
  return acc;
}, {});

const sanitizeGridItem = (item = {}) => pickKeys(item, [
  'id',
  'h',
  'w',
  'x',
  'y',
  'pos',
  'data',
  'minH',
  'minW',
  'name',
  'type',
  'type2',
  'content',
]);

const sanitizeSlot = (slot = {}) => pickKeys(slot, [
  'id',
  'pos',
  'data',
  'name',
  'type',
  'type2',
  'minH',
  'minW',
  'h',
  'w',
  'x',
  'y',
  'content',
]);

const sanitizeViewPayload = (view = {}) => {
  const sanitizedGridItems = Array.isArray(view.gridItems)
    ? view.gridItems.map((item) => sanitizeGridItem(item))
    : [];

  const sanitizedSlots = Array.isArray(view.slots)
    ? view.slots.map((slot) => sanitizeSlot(slot))
    : [];

  const sanitizedHiddenSlots = Array.isArray(view.hiddenSlots)
    ? view.hiddenSlots.map((slot) => sanitizeSlot(slot))
    : [];

  return {
    id: view.id,
    name: view.name,
    layout: view.layout !== undefined && view.layout !== null
      ? sanitizeValue(view.layout)
      : 'default',
    type: view.type || 'video-tour',
    isCard: view.is_card ?? view.isCard ?? 0,
    distance: view.distance ?? 0,
    gridItems: sanitizedGridItems,
    slots: sanitizedSlots,
    hiddenSlots: sanitizedHiddenSlots,
  };
};

const normalizeLink = (link) => {
  if (!link) return null;
  if (/^https?:\/\//i.test(link)) return link;
  return link.replace(/^\/+/, '');
};

const notifyError = (message) => {
  if (!message) return;
  if (Vue.$toast) {
    Vue.$toast({
      component: ToastificationContent,
      props: {
        title: 'Video Tour',
        text: message,
        icon: 'AlertTriangleIcon',
        variant: 'danger',
      },
    });
  }
};

const buildTabVideoTourState = (state, overrides = {}) => {
  if (!state.currentTourId) return null;

  return {
    id: state.currentTourId,
    currentIndex: state.currentIndex,
    count: state.count,
    intervalSecs: state.intervalSecs,
    active: state.active,
    isPaused: state.isPaused,
    ...overrides,
  };
};

const persistTabVideoTourState = (commit, state, overrides = {}) => {
  if (!state.targetTabId) return;
  const videoTourState = buildTabVideoTourState(state, overrides);
  if (!videoTourState) return;

  commit('setTabVideoTourState', {
    tabId: state.targetTabId,
    videoTour: videoTourState,
  });
};

export default {
  state,
  getters,
  mutations,
  actions,
};
